import {
  AUTH_LOGIN,
  AUTH_SUCCESS,
  AUTH_ERROR,
  AUTH_LOGOUT,
  AUTH_PURGE,
  AUTH_RESET_EMAIL,
  AUTH_RESET_REQUEST,
  // CHECK_AUTH,
  AUTH_RESET_EMAIL_CLEAR,
  AUTH_RESET_REQUEST_CLEAR,
  AUTH_TOKEN_REFRESH,
  AUTH_TOKEN_REFRESH_INTERVAL,
  PASSWORD_RESET,
  SSO_LOGIN,
  PREV_ROUTE
} from './auth.type'

import ApiRest, { SetApiHeaderWithRefreshToken, SetApiHeaderWithToken } from '@/api/api-rest'

import { registerInverval, clearAllInterval } from '@/utilities/global.interval'

import { registerLocalStorage, clearLocalStorage } from '@/utilities/localstorage.helper'

const state = {
  errors: null,
  token: localStorage.getItem('user_token') || '',
  tokenRefresh: 600000, // in millisec - default is 10 mins
  passwordEmailSentStatus: false,
  passwordEmailSentMessage: false,
  passwordTokenResetStatus: false,
  passwordTokenResetMessage: '',
  passwordResetStatus: false,
  passwordResetMessage: '',
  prevRoute: {}
}

const getters = {
  isAuthenticated: state => state.token,
  authError: state => state.errors,
  isPasswordEmailSent: state => state.passwordEmailSentStatus,
  passwordEmailSentMessage: state => state.passwordEmailSentMessage,
  passwordTokenResetStatus: state => state.passwordTokenResetStatus,
  passwordTokenResetMessage: state => state.passwordTokenResetMessage,
  passwordResetStatus: state => state.passwordResetStatus,
  passwordResetMessage: state => state.passwordResetMessage,
  prevRoute: state => state.prevRoute
}

const mutations = {
  [AUTH_ERROR] (state, error) {
    state.errors = error
    state.token = localStorage.getItem('user_token') || ''
  },
  [AUTH_SUCCESS] (state, data) {
    state.errors = {}
    state.token = data['access_token']
  },
  [AUTH_PURGE] (state) {
    state.user = {}
    state.errors = ''
    state.token = ''
  },
  [AUTH_RESET_EMAIL] (state, data) {
    state.passwordEmailSentStatus = data.status
    state.passwordEmailSentMessage = data.message
  },
  [AUTH_RESET_EMAIL_CLEAR] (state) {
    state.passwordEmailSentStatus = false
    state.passwordEmailSentMessage = ''
  },
  [AUTH_RESET_REQUEST] (state, data) {
    state.passwordTokenResetStatus = data.status
    state.passwordTokenResetMessage = data.message
  },
  [AUTH_RESET_REQUEST_CLEAR] () {
    state.passwordTokenResetStatus = false
    state.passwordTokenResetMessage = ''
  },
  [AUTH_TOKEN_REFRESH] (state, data) {
    state.token = data['access_token']
  },
  [PASSWORD_RESET] (state, data) {
    state.passwordResetStatus = data.status
    state.passwordTokenResetMessage = data.message
  },
  [PREV_ROUTE] (state, data) {
    state.prevRoute = data
  }
}

const actions = {
  [SSO_LOGIN] (context, credentials) {
    localStorage.setItem('user_token', credentials['accessToken'])
    SetApiHeaderWithToken()
    return new Promise((resolve, reject) => {
      ApiRest()
        .post('/single-sign-on', {
          firstName: credentials.firstName,
          lastName: credentials.lastName,
          email: credentials.email,
          accessToken: credentials.accessToken
        })
        .then(({ data }) => {
          if (data) {
            registerLocalStorage(data)
            SetApiHeaderWithToken()

            context.commit(AUTH_SUCCESS, {
              access_token: data['access_token'],
              expires: data.expires
            })
          }
          resolve(data)
        })
        .catch(({ data }) => {
          context.commit(AUTH_ERROR, data ? data.message : data)
          clearLocalStorage()
          reject(data)
        })
    })
  },
  [AUTH_LOGIN] (context, credentials) {
    return new Promise((resolve, reject) => {
      ApiRest()
        .post('/login', {
          email: credentials.email,
          password: credentials.password
        })
        .then(({ data }) => {
          if (data) {
            registerLocalStorage(data)
            SetApiHeaderWithToken()

            context.commit(AUTH_SUCCESS, {
              access_token: data['access_token'],
              expires: data.expires
            })
          }
          resolve(data)
        })
        .catch(({ data }) => {
          context.commit(AUTH_ERROR, data ? data.message : data)
          clearLocalStorage()
          reject(data)
        })
    })
  },
  [AUTH_SUCCESS] (context, token) {
    registerLocalStorage(token)
    SetApiHeaderWithToken()

    context.commit(AUTH_SUCCESS, token)
  },
  [AUTH_LOGOUT] (context) {
    return new Promise((resolve, reject) => {
      ApiRest()
        .post('/logout/access')
        .then(({ data }) => {
          clearLocalStorage()
          context.commit(AUTH_PURGE)
          clearAllInterval()
          resolve(data)
        })
        .catch(({ data }) => {
          clearLocalStorage()
          context.commit(AUTH_PURGE)
          clearAllInterval()
          reject(data)
        })
    })
  },
  [AUTH_RESET_EMAIL] (context, credentials) {
    return new Promise((resolve, reject) => {
      ApiRest()
        .post('/request-reset', {
          email: credentials.email
        })
        .then(({ data }) => {
          context.commit(AUTH_RESET_EMAIL, {
            status: true,
            message: data.message
          })
          resolve(data)
        })
        .catch(({ data }) => {
          context.commit(AUTH_RESET_EMAIL, {
            status: false,
            message: data ? data.message : data
          })
          reject(data)
        })
    })
  },
  [AUTH_RESET_REQUEST] (context, credentials) {
    if (credentials.token) {
      ApiRest()
        .post(`/reset/${credentials.token}`, {
          password: credentials.password
        })
        .then(({ data }) => {
          context.commit(AUTH_RESET_REQUEST, {
            status: true,
            message: data.message
          })
        })
        .catch(({ data }) => {
          context.commit(AUTH_RESET_REQUEST, {
            status: false,
            message: data ? data.message : data
          })
        })
    }
  },
  [AUTH_TOKEN_REFRESH] (context) {
    return new Promise((resolve, reject) => {
      // Set API header bearer token to refresh token for this request
      SetApiHeaderWithRefreshToken()

      ApiRest()
        .post('/token/refresh')
        .then(({ data }) => {
          // only need to update access token - other local storage keys (refresh_token & token_exp) remain the same
          localStorage.setItem('user_token', data['access_token'])
          SetApiHeaderWithToken()
          context.commit(AUTH_TOKEN_REFRESH, data)
          resolve(data)
        })
        .catch(({ data }) => {
          reject(data)
        })

      // Reset API header bearer token to access token (bc this is an async request - other request may go out and need bearer access token)
      SetApiHeaderWithToken()
    })
  },
  [AUTH_TOKEN_REFRESH_INTERVAL] (context) {
    let intervalPeriod = localStorage.getItem('token_exp_timer') || state.tokenRefresh
    let intervalId = setInterval(() => {
      // Set API header bearer token to refresh token for this request
      SetApiHeaderWithRefreshToken()

      ApiRest()
        .post('/token/refresh')
        .then(({ data }) => {
          // only need to update access token - other local storage keys (refresh_token & token_exp) remain the same
          localStorage.setItem('user_token', data['access_token'])
          SetApiHeaderWithToken()
          context.commit(AUTH_TOKEN_REFRESH, data)
        })
        .catch(({ data }) => {
          // do nothing for now
        })

      // Reset API header bearer token to access token (bc this is an async request - other request may go out and need bearer access token)
      SetApiHeaderWithToken()
    }, intervalPeriod)

    registerInverval(intervalId)
  },
  [AUTH_RESET_EMAIL_CLEAR] (context) {
    context.commit(AUTH_RESET_EMAIL_CLEAR)
  },
  [AUTH_RESET_REQUEST_CLEAR] (context) {
    context.commit(AUTH_RESET_REQUEST_CLEAR)
  },
  [PASSWORD_RESET] (context, credentials) {
    return new Promise((resolve, reject) => {
      ApiRest()
        .put('/reset', {
          current_password: credentials.currentPassword,
          new_password: credentials.newPassword,
          confirm_new_password: credentials.confirmNewPassword,
          email: credentials.email
        })
        .then(({ data }) => {
          context.commit(PASSWORD_RESET, {
            status: true,
            message: data.message
          })
          resolve(data)
        })
        .catch(({ data }) => {
          context.commit(PASSWORD_RESET, {
            status: false,
            message: data ? data.message : data
          })
          reject(data)
        })
    })
  }
}

export default {
  state,
  actions,
  mutations,
  getters
}



// WEBPACK FOOTER //
// ./src/store/modules/auth/auth.module.js